/**
 * PublicLeaderboardPage — `/leaderboard/:token`
 *
 * The shareable district board. No login: anyone with the link sees the live
 * rankings and can grab the PNG for WhatsApp.
 */
import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Trophy, Coins, Users, UserCheck, Download, Share2, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MEDALS = ['bg-amber-400 text-amber-950', 'bg-slate-300 text-slate-800', 'bg-orange-300 text-orange-950'];
const money = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const PublicLeaderboardPage = () => {
  const { token } = useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-leaderboard', token],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/public/leaderboard/${token}`);
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'This link is no longer active');
      return r.json();
    },
    retry: false,
  });

  const imageUrl = `${API_URL}/api/public/leaderboard/${token}/card.png`;
  const shareText = data ? `${data.scope?.name} district leaderboard — ${data.share?.period_label}` : '';

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-950 px-6"
           data-testid="public-leaderboard-error">
        <div className="text-center">
          <Trophy className="mx-auto mb-3 h-9 w-9 text-slate-700" />
          <p className="text-slate-300">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-16" data-testid="public-leaderboard-page">
      <header className="bg-slate-900 px-6 py-12">
        <div className="mx-auto max-w-3xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            District leaderboard
          </p>
          <h1 className="mt-2 text-3xl font-bold text-white sm:text-4xl"
              data-testid="public-leaderboard-scope">
            {isLoading ? <Skeleton className="h-9 w-64 bg-slate-800" /> : data?.scope?.name}
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            {data?.share?.period_label} · giving in {data?.reporting_currency}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Button asChild size="sm" className="bg-white text-slate-900 hover:bg-slate-200"
                    data-testid="public-leaderboard-download">
              <a href={imageUrl} download={`leaderboard-${token}.png`}>
                <Download className="mr-1.5 h-4 w-4" /> Download image
              </a>
            </Button>
            <Button asChild size="sm" variant="outline"
                    className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800"
                    data-testid="public-leaderboard-whatsapp">
              <a href={`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${window.location.href}`)}`}
                 target="_blank" rel="noreferrer">
                <Share2 className="mr-1.5 h-4 w-4" /> Share on WhatsApp
              </a>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 px-6 py-8">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[['Districts', data?.totals?.districts], ['Assemblies', data?.totals?.assemblies],
            ['Members', data?.totals?.members],
            [`Giving`, money(data?.totals?.giving)]].map(([label, value]) => (
            <div key={label} className="rounded-xl bg-slate-900 p-4"
                 data-testid={`public-total-${label.toLowerCase()}`}>
              <p className="text-xl font-bold tabular-nums text-white">{value ?? 0}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
            </div>
          ))}
        </div>

        <Card className="border-slate-800 bg-slate-900">
          <CardContent className="p-0">
            {isLoading ? (
              <div className="space-y-2 p-4">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full bg-slate-800" />)}
              </div>
            ) : (data?.rows || []).length === 0 ? (
              <p className="py-16 text-center text-sm text-slate-400" data-testid="public-leaderboard-empty">
                No districts in this branch yet.
              </p>
            ) : (
              <div className="divide-y divide-slate-800">
                {data.rows.map((d, i) => {
                  const up = d.growth_pct > 0;
                  const Icon = d.growth_pct === 0 ? Minus : up ? TrendingUp : TrendingDown;
                  return (
                    <div key={d.id} className="flex flex-wrap items-center gap-4 px-4 py-3"
                         data-testid={`public-leaderboard-row-${d.id}`}>
                      <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-bold ${
                        MEDALS[i] || 'bg-slate-800 text-slate-400'}`}>{d.rank}</span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-white">{d.name}</p>
                        <p className="text-[11px] text-slate-500">
                          {d.assemblies} assembl{d.assemblies === 1 ? 'y' : 'ies'} · +{d.new_members} joined
                        </p>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-300">
                        <span className="flex items-center gap-1 tabular-nums">
                          <Coins className="h-3.5 w-3.5 text-slate-500" />{money(d.giving)}
                        </span>
                        <span className="flex items-center gap-1 tabular-nums">
                          <UserCheck className="h-3.5 w-3.5 text-slate-500" />{d.attendance_avg}
                        </span>
                        <span className="flex items-center gap-1 tabular-nums">
                          <Users className="h-3.5 w-3.5 text-slate-500" />{d.members}
                        </span>
                        <Badge variant="outline"
                               className={`border-0 text-[11px] ${up ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-800 text-slate-400'}`}>
                          <Icon className="mr-1 h-3 w-3" />{up ? '+' : ''}{d.growth_pct}%
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <img src={imageUrl} alt="Leaderboard card"
             className="mx-auto w-full max-w-sm rounded-xl border border-slate-800"
             data-testid="public-leaderboard-image" />
        <p className="text-center text-[11px] text-slate-600">
          Ranked by giving, then by new members. Growth compares this window with the one before it.
        </p>
      </main>
    </div>
  );
};

export default PublicLeaderboardPage;
